import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getCookieConsent } from "@/components/CookieConsent";

const GA_ID = "G-PP0C40TKZR";

function loadGA() {
  if (window.__gaLoaded) return;
  window.__gaLoaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  // send_page_view:false — we send SPA pageviews manually (pathname only, no query/PII).
  window.gtag("config", GA_ID, { anonymize_ip: true, send_page_view: false });
}

// LinkedIn Insight Tag (partner id 5565412). Advertising/retargeting tag, so it is
// loaded ONLY after the visitor accepts marketing cookies (GDPR). No <noscript> pixel
// is used, since that would fire unconditionally without consent.
const LINKEDIN_PARTNER_ID = "5565412";

// Maps internal event names -> LinkedIn conversion IDs (from Campaign Manager).
// Fires window.lintrk('track', {conversion_id}) on marketing consent only.
const LI_CONVERSIONS = {
  company_analysis_started: 31049329, // Analyse påbegyndt (event-specifik)
  sign_up: 30786337,        // Oprettelse / sign-up (alle nye konti)
  abonnement_koebt: 30786345, // Abonnement betalt
};

// Maps internal event names -> Meta (Facebook) STANDARD events. Fires on marketing consent.
const META_EVENTS = {
  company_analysis_started: "Lead", // Analyse påbegyndt (standard event => optimerbar i Meta)
  sign_up: "CompleteRegistration",
  begin_checkout: "InitiateCheckout",
  abonnement_koebt: "Purchase",
};

// Maps internal event names -> Meta CUSTOM events (fbq trackCustom). Fires on marketing consent.
// For custom events skal du selv oprette en Custom Conversion i Meta Events Manager.
const META_CUSTOM_EVENTS = {
  company_analysis_completed: "AnalyseGennemfoert", // Analyse gennemført
};

function loadLinkedIn() {
  if (window.__liLoaded) return;
  window.__liLoaded = true;
  window._linkedin_partner_id = LINKEDIN_PARTNER_ID;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(LINKEDIN_PARTNER_ID);
  (function (l) {
    if (!l) {
      window.lintrk = function (a, b) { window.lintrk.q.push([a, b]); };
      window.lintrk.q = [];
    }
    const s = document.getElementsByTagName("script")[0];
    const b = document.createElement("script");
    b.type = "text/javascript";
    b.async = true;
    b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
    s.parentNode.insertBefore(b, s);
  })(window.lintrk);
}

// Meta (Facebook) Pixel. Advertising tag — loaded ONLY after marketing consent (GDPR).
// No <noscript> pixel is used, since it would fire unconditionally without consent.
const META_PIXEL_ID = "1902448143331135";

function loadMeta() {
  if (window.__fbqLoaded) return;
  window.__fbqLoaded = true;
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  window.fbq("init", META_PIXEL_ID);
  window.fbq("track", "PageView");
}

// (window.gtag only exists after loadGA runs on consent). Never pass PII / URLs /
// prompts / company data / AI output / payment info. GA4 event names use snake_case.
export function trackEvent(name, params = {}) {
  if (typeof window === "undefined") return;
  // Respect current consent on EVERY call: covers first-visit accept, already-stored
  // consent (loads GA if the parent effect hasn't run yet) and live revocation.
  const c = getCookieConsent();
  const allowed = !!(c && c.statistics);
  postTrack(name, allowed); // first-party capture ALWAYS (cookieless when no consent)
  // Advertising conversions — fire on MARKETING consent, independent of GA4/statistics.
  if (c && c.marketing) {
    if (LI_CONVERSIONS[name]) {
      if (!window.__liLoaded) loadLinkedIn();
      if (window.lintrk) window.lintrk("track", { conversion_id: LI_CONVERSIONS[name] });
    }
    if (META_EVENTS[name] || META_CUSTOM_EVENTS[name]) {
      if (!window.__fbqLoaded) loadMeta();
      if (window.fbq) {
        const p = { content_name: name }; // fx "company_analysis_started" — genkendeligt i Meta
        if (params.value != null) p.value = params.value;
        if (params.currency) p.currency = params.currency;
        if (META_EVENTS[name]) window.fbq("track", META_EVENTS[name], p);
        if (META_CUSTOM_EVENTS[name]) window.fbq("trackCustom", META_CUSTOM_EVENTS[name], p);
      }
    }
  }
  if (!allowed) return; // GA4/Clarity only with statistics consent
  if (!window.__gaLoaded) loadGA();
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[GA4]", name, params, window.__gaLoaded ? "(sent)" : "(no consent yet)");
  }
  if (window.__gaLoaded && window.gtag) {
    window.gtag("event", name, params);
  }
}

const ATTR_KEY = "aicom_attr";
const AID_KEY = "aicom_aid";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// First-party anonymous id. With statistics consent: a persistent cross-session id
// (localStorage). Without consent: a NON-persistent per-tab session id (sessionStorage,
// cleared when the tab closes) so we get cookieless aggregate analytics with no
// persistent identifier and no PII.
export function anonymousId(consented) {
  try {
    if (consented) {
      let id = localStorage.getItem(AID_KEY);
      if (!id) { id = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`); localStorage.setItem(AID_KEY, id); }
      return id;
    }
    let sid = sessionStorage.getItem(AID_KEY);
    if (!sid) { sid = "s-" + (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`); sessionStorage.setItem(AID_KEY, sid); }
    return sid;
  } catch { return "s-" + Math.random().toString(36).slice(2); }
}

// Fire-and-forget first-party event capture (independent of GA4/adblockers/consent).
// beacon=true uses navigator.sendBeacon so the request survives page unload (tab close /
// full navigation) — used for the page_dwell "time on page" event.
function postTrack(name, consented, extra = {}, beacon = false) {
  try {
    const a = getAttribution();
    const aid = anonymousId(consented);
    if (!aid || !BACKEND) return;
    const url = `${BACKEND}/api/track`;
    const body = JSON.stringify({
      event: name, anonymous_id: aid, consented: !!consented,
      utm_source: a.utm_source || "", utm_medium: a.utm_medium || "",
      utm_campaign: a.utm_campaign || "", landing_page: a.landing_page || "",
      path: window.location.pathname,
      ...extra,
    });
    if (beacon && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      return;
    }
    fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, keepalive: true, body }).catch(() => {});
  } catch { /* ignore */ }
}

// First-party attribution captured once on the first visit and kept through onboarding.
export function getAttribution() {
  try { return JSON.parse(localStorage.getItem(ATTR_KEY) || "{}"); } catch { return {}; }
}

export function utmParams() {
  const a = getAttribution();
  const o = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((k) => { if (a[k]) o[k] = a[k]; });
  return o;
}

// GA4 client_id from the _ga cookie (last two numeric parts of "GA1.1.x.y").
// Only present after the browser GA tag has loaded (i.e. after statistics consent).
export function gaClientId() {
  try {
    const raw = document.cookie.split("; ").find((c) => c.startsWith("_ga="))?.split("=")[1];
    if (!raw) return null;
    const parts = decodeURIComponent(raw).split(".");
    return parts.length >= 4 ? `${parts[parts.length - 2]}.${parts[parts.length - 1]}` : null;
  } catch { return null; }
}

function captureAttribution() {
  try {
    if (localStorage.getItem(ATTR_KEY)) return;
    const p = new URLSearchParams(window.location.search);
    const attr = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((k) => { const v = p.get(k); if (v) attr[k] = v; });
    attr.landing_page = window.location.pathname;
    attr.referrer = document.referrer || "";
    localStorage.setItem(ATTR_KEY, JSON.stringify(attr));
  } catch (e) { /* ignore */ }
}

// Canonical plan identifiers used across all GA4 ecommerce events + numeric DKK prices.
const PLAN_IDS = {
  gratis: "free", free: "free",
  start: "starter_395", starter_395: "starter_395",
  pro: "pro_995", pro_995: "pro_995",
  business: "business_2495", business_2495: "business_2495",
};
const PLAN_PRICES = { free: 0, starter_395: 395, pro_995: 995, business_2495: 2495 };

export function planId(key) { return PLAN_IDS[key] || String(key || ""); }
export function planPrice(key) { const id = planId(key); return PLAN_PRICES[id] ?? 0; }
// GA4 items[] entry with canonical id + numeric price (no currency symbol).
export function planItem(key) { const id = planId(key); return { item_id: id, price: PLAN_PRICES[id] ?? 0 }; }

// Loads Google Analytics only after the visitor accepts statistics cookies (GDPR).
export default function Analytics() {
  const location = useLocation();
  const [ready, setReady] = useState(false);
  const lastPath = useRef(null);

  useEffect(() => {
    captureAttribution();
    const enable = () => { loadGA(); setReady(true); };
    const c = getCookieConsent();
    if (c && c.statistics) enable();
    if (c && c.marketing) { loadLinkedIn(); loadMeta(); }
    const onConsent = (e) => {
      if (e.detail && e.detail.statistics) enable();
      if (e.detail && e.detail.marketing) { loadLinkedIn(); loadMeta(); }
    };
    window.addEventListener("cookie-consent-updated", onConsent);
    return () => window.removeEventListener("cookie-consent-updated", onConsent);
  }, []);

  // Manual SPA pageviews (auto pageview is disabled in config). Exactly one per real
  // route change, pathname only — never the query string (avoids leaking tokens/PII).
  // Re-checks consent so pageviews stop immediately if the visitor revokes statistics.
  useEffect(() => {
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;
    const c = getCookieConsent();
    const consented = !!(c && c.statistics);
    // First-party pageview — ALWAYS captured (cookieless when no consent).
    postTrack(location.pathname === "/" ? "landing_viewed" : "page_view", consented);
    // GA4 pageview only with statistics consent.
    if (ready && consented && window.gtag) {
      window.gtag("event", "page_view", { page_path: location.pathname, page_title: document.title });
    }
    // Meta Pixel SPA pageview (marketing consent only).
    if (c && c.marketing && window.fbq) {
      window.fbq("track", "PageView");
    }
  }, [ready, location]);

  // Dwell time: how long the visitor stays on each page (esp. /kom-i-gang). Sent as a
  // first-party "page_dwell" event on route change and when the tab is hidden/closed.
  const enterRef = useRef({ path: location.pathname, t: Date.now() });
  const flushDwell = (nextPath, beacon = false) => {
    const prev = enterRef.current;
    const dur = Date.now() - prev.t;
    // Record from 300ms so fast bounces (<5s) are captured; cap 30 min to drop idle tabs.
    if (dur > 300 && dur < 30 * 60 * 1000) {
      const c = getCookieConsent();
      postTrack("page_dwell", !!(c && c.statistics), { duration_ms: dur, path: prev.path }, beacon);
    }
    enterRef.current = { path: nextPath ?? prev.path, t: Date.now() };
  };
  useEffect(() => {
    if (enterRef.current.path !== location.pathname) flushDwell(location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  useEffect(() => {
    const onPageHide = () => flushDwell(undefined, true);
    const onHide = () => { if (document.visibilityState === "hidden") flushDwell(undefined, true); };
    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
